// Email HTML is not sanitized (rewritten) in the frontend: silently removing markup surprises
// the customer and rewriting with DOMPurify destroyed full email documents - doctype, <head>,
// <style>/media queries. Instead unsafe markup is detected and reported so the customer
// removes it themselves (see findUnsafeMarkup). Previews render the HTML inside sandboxed
// iframes (sandbox="", srcDoc) so nothing in it can run or restyle the app.

// Fast client-side UX check used by the SMS/RCS/WhatsApp/test-seed validators.
// The event-handler branch requires "on" to start an attribute name (preceded by
// whitespace or string start) so legitimate attributes like content=/action= that
// merely contain "on" mid-word (e.g. meta's content=) aren't misflagged.
const DANGEROUS_MARKUP_PATTERN = /<script[\s>]|(?:^|\s)on\w+\s*=/i

export function containsUnsafeMarkup(value) {
  return typeof value === 'string' && DANGEROUS_MARKUP_PATTERN.test(value)
}

const UNSAFE_ELEMENTS = ['script', 'iframe', 'frame', 'object', 'embed', 'applet', 'base']
const URL_ATTRIBUTES = ['href', 'src', 'action', 'formaction', 'xlink:href', 'background', 'poster', 'data']
const UNSAFE_URL_PATTERN = /^(javascript:|vbscript:|data:text\/html)/
const UNSAFE_CSS_PATTERN = /javascript:|expression\s*\(/i

// Browsers ignore whitespace/control characters inside a URL scheme ("jav\tascript:").
const normalizeUrl = value =>
  value
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000- \u007f]/g, '')
    .toLowerCase()

/**
 * Lists the unsafe markup in an HTML fragment or full document (including <head>):
 * script-capable elements, on* event handlers, javascript:/vbscript:/data:text/html URLs,
 * scriptable CSS and javascript: meta refreshes. Nothing is modified.
 * @param {string} html
 * @returns {string[]} human-readable problems, e.g. "onerror attribute on <img>"; [] when safe
 */
export function findUnsafeMarkup(html) {
  if (typeof html !== 'string' || html.trim() === '') {
    return []
  }
  if (typeof window === 'undefined' || !window.DOMParser) {
    return containsUnsafeMarkup(html) ? ['script or event-handler markup'] : []
  }

  const doc = new window.DOMParser().parseFromString(html, 'text/html')
  const issues = new Set()

  doc.querySelectorAll('*').forEach(el => {
    const tag = el.tagName.toLowerCase()
    if (UNSAFE_ELEMENTS.includes(tag)) {
      issues.add(`<${tag}> tag`)
    }
    if (tag === 'style' && UNSAFE_CSS_PATTERN.test(el.textContent)) {
      issues.add('javascript: or expression() in a <style> block')
    }
    if (
      tag === 'meta' &&
      (el.getAttribute('http-equiv') || '').toLowerCase() === 'refresh' &&
      normalizeUrl(el.getAttribute('content') || '').includes('javascript:')
    ) {
      issues.add('javascript: URL in a <meta> refresh')
    }

    Array.from(el.attributes).forEach(({ name, value }) => {
      const attr = name.toLowerCase()
      if (attr.startsWith('on')) {
        issues.add(`${attr} attribute on <${tag}>`)
      } else if (URL_ATTRIBUTES.includes(attr)) {
        const scheme = normalizeUrl(value).match(UNSAFE_URL_PATTERN)
        if (scheme) {
          issues.add(`${scheme[1]} URL in ${attr} on <${tag}>`)
        }
      } else if (attr === 'style' && UNSAFE_CSS_PATTERN.test(value)) {
        issues.add(`javascript: or expression() in the style of <${tag}>`)
      }
    })
  })

  return Array.from(issues)
}

/** Validation message listing what the customer has to remove. */
export const formatUnsafeMarkupError = issues => `Remove unsafe markup before saving: ${issues.join(', ')}`
